/**
 * Full app preview page — renders the complete app shell with mock data.
 *
 * Uses the workshop master layout and layout provider to give designers
 * a full preview of how the complete application looks and behaves.
 * The canvas area shows a grid pattern placeholder.
 * Clicking the settings gear or user avatar opens the settings modal.
 */

import { useState } from "react";
import { useCanvasTransform } from "@/workshop/components/canvas/canvas-viewport";
import { WorkshopLayoutProvider } from "@/workshop/components/layout/layout-provider";
import { WorkshopMasterLayout } from "@/workshop/components/layout/master-layout";
import { SettingsModal } from "@/workshop/components/settings/settings-modal";
import type { SettingsTab } from "@/workshop/components/settings/settings-modal";
import { ProfileSection } from "@/workshop/components/settings/profile-section";
import { BillingSection } from "@/workshop/components/settings/billing-section";
import { ConnectedAccountsSection } from "@/workshop/components/settings/connected-accounts-section";
import { AgentsSection } from "@/workshop/components/settings/agents-section";
import { ProjectsSection } from "@/workshop/components/settings/projects-section";
import { AppearanceSection } from "@/workshop/components/settings/appearance-section";
import { NotificationsSection } from "@/workshop/components/settings/notifications-section";
import { ContactSection } from "@/workshop/components/settings/contact-section";
import { HelpSection } from "@/workshop/components/settings/help-section";
import { CompanionSection } from "@/workshop/components/settings/companion-section";

/* ---------- Settings content renderer ---------- */

function renderSettingsContent(tab: SettingsTab): React.ReactNode {
  switch (tab) {
    case "profile":
      return <ProfileSection />;
    case "billing":
      return <BillingSection />;
    case "connected-accounts":
      return <ConnectedAccountsSection />;
    case "companion":
      return <CompanionSection />;
    case "agents":
      return <AgentsSection />;
    case "projects":
      return <ProjectsSection />;
    case "appearance":
      return <AppearanceSection />;
    case "notifications":
      return <NotificationsSection />;
    case "contact":
      return <ContactSection />;
    case "help":
      return <HelpSection />;
  }
}

/* ---------- Mock canvas placeholder ---------- */

function CanvasPlaceholder() {
  const { zoom } = useCanvasTransform();
  const dotR = `${1 / zoom}px`;
  const spacing = `${24 / zoom}px`;

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-white dark:bg-black"
      data-testid="canvas-placeholder"
      style={{
        backgroundImage: `radial-gradient(circle, var(--color-canvas-dot) ${dotR}, transparent ${dotR})`,
        backgroundSize: `${spacing} ${spacing}`,
      }}
    />
  );
}

/* ---------- Main page ---------- */

interface FullAppPageProps {
  /** Seeds the input bar with this value on mount (used by demo walkthrough). */
  defaultInputValue?: string;
  /** Called when the input bar is submitted (used by demo walkthrough). */
  onInputSubmit?: () => void;
  /** When true, the input bar is rendered in a disabled state. */
  disabledInput?: boolean;
}

export function FullAppPage({
  defaultInputValue,
  onInputSubmit,
  disabledInput,
}: FullAppPageProps = {}) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<SettingsTab>("profile");

  function openSettings(tab: SettingsTab = "profile") {
    setSettingsTab(tab);
    setSettingsOpen(true);
  }

  return (
    <WorkshopLayoutProvider
      defaultSidebarOpen={true}
      defaultRightPanelOpen={true}
    >
      {/*
        SVG liquid-glass distortion filter — must live in the DOM for
        `backdrop-filter: url(#liquid-glass-distortion)` in index.css to work.
      */}
      <svg style={{ display: "none" }} aria-hidden="true">
        <defs>
          <filter
            id="liquid-glass-distortion"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.018 0.014"
              numOctaves="3"
              seed="5"
              result="noise"
            />
            <feColorMatrix
              type="saturate"
              values="0"
              in="noise"
              result="grayNoise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="grayNoise"
              scale="2"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>
      <div
        className="h-screen w-screen overflow-hidden"
        data-testid="workshop-full-app-page"
      >
        <WorkshopMasterLayout
          onUserMenu={() => openSettings("profile")}
          defaultInputValue={defaultInputValue}
          onInputSubmit={onInputSubmit}
          disabledInput={disabledInput}
        >
          <CanvasPlaceholder />
        </WorkshopMasterLayout>

        <SettingsModal
          open={settingsOpen}
          activeTab={settingsTab}
          onTabChange={setSettingsTab}
          onClose={() => setSettingsOpen(false)}
          renderContent={renderSettingsContent}
        />
      </div>
    </WorkshopLayoutProvider>
  );
}
